/** Public header. Behavior names must match Client/Pipelines/SiteNav/SiteNavCatalog.cs. */

export type SiteNavBehavior = "in-office" | "resource-center";

export interface SiteNavLink {
  id: string;
  label: string;
  href: string;
  /** Keeps the account check when the label or link is changed. */
  behavior?: SiteNavBehavior;
  children: SiteNavLink[];
}

export function defaultSiteNav(): SiteNavLink[] {
  return [
    {
      id: "place-order",
      label: "Place an Order",
      href: "/catalog",
      children: [
        { id: "how-ordering-works", label: "How Our Order Processing Works", href: "/process", children: [] },
        { id: "patient-order", label: "Patient-Specific Order", href: "/dashboard/patients", children: [] },
        { id: "in-office", label: "In-Office Order", href: "/catalog?loc=office", behavior: "in-office", children: [] },
        { id: "order-templates", label: "Order Templates", href: "/dashboard/order-templates", children: [] },
        { id: "order-history", label: "My Order History", href: "/dashboard/orders", children: [] },
        { id: "pending-orders", label: "Pending Orders", href: "/dashboard/pending-orders", children: [] },
        { id: "favorites", label: "Favorites", href: "/dashboard/favorites", children: [] },
      ],
    },
    {
      id: "product-menu",
      label: "My Product Menu",
      href: "/catalog",
      children: [
        { id: "browse-products", label: "Browse Products", href: "/catalog", children: [] },
        { id: "efficacy", label: "Product Efficacy & Quality", href: "/efficacy", children: [] },
      ],
    },
    { id: "dashboard", label: "My Dashboard", href: "/dashboard", children: [] },
    { id: "about", label: "About Us", href: "/about", children: [] },
    { id: "resource-center", label: "Resource Center", href: "/resources", behavior: "resource-center", children: [] },
  ];
}

export function newNavId(): string {
  return `link-${Math.random().toString(36).slice(2, 10)}`;
}

export function isSafeHref(href: string): boolean {
  const value = href.trim();
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return false;
  if (value.length > 300 || /[\s\\]/.test(value) || /[\u0000-\u001f]/.test(value)) return false;
  return true;
}

function field(source: Record<string, unknown>, name: string): unknown {
  if (name in source) return source[name];
  const pascal = name.charAt(0).toUpperCase() + name.slice(1);
  return source[pascal];
}

function readLink(value: unknown, allowChildren: boolean): SiteNavLink | null {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;
  const id = field(source, "id");
  const label = field(source, "label");
  const href = field(source, "href");
  if (typeof id !== "string" || typeof label !== "string" || typeof href !== "string") return null;
  if (!/^[a-z0-9-]{1,40}$/.test(id) || !label.trim() || !isSafeHref(href)) return null;
  const rawBehavior = field(source, "behavior");
  const behavior = rawBehavior === "in-office" || rawBehavior === "resource-center" ? rawBehavior : undefined;
  const children: SiteNavLink[] = [];
  if (allowChildren) {
    const rawChildren = field(source, "children");
    if (Array.isArray(rawChildren)) {
      for (const child of rawChildren) {
        const link = readLink(child, false);
        if (link) children.push(link);
      }
    }
  }
  return { id, label: label.trim(), href: href.trim(), ...(behavior ? { behavior } : {}), children };
}

/** Saved menu, or the built-in one when nothing valid has been stored. Accepts the API's PascalCase payload. */
export function resolveSiteNav(raw: unknown): SiteNavLink[] {
  if (!raw || typeof raw !== "object") return defaultSiteNav();
  const items = field(raw as Record<string, unknown>, "items");
  if (!Array.isArray(items)) return defaultSiteNav();
  const links = items.map((item) => readLink(item, true)).filter((item): item is SiteNavLink => item !== null);
  return links.length ? links : defaultSiteNav();
}

export function findNavLink(items: SiteNavLink[], id: string): SiteNavLink | undefined {
  for (const item of items) {
    if (item.id === id) return item;
    const child = item.children.find((entry) => entry.id === id);
    if (child) return child;
  }
  return undefined;
}

export function toSiteNavPayload(items: SiteNavLink[]) {
  const link = (item: SiteNavLink): Record<string, unknown> => ({
    Id: item.id,
    Label: item.label.trim(),
    Href: item.href.trim(),
    Behavior: item.behavior ?? null,
    Children: item.children.map(link),
  });
  return { Items: items.map(link) };
}
